
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import NavigationBar from "@/components/landing/NavigationBar";
import Footer from "@/components/landing/Footer";
import { CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSubscriptionManager } from "@/hooks/useSubscriptionManager";
import { UserType } from "@/types/user";
import { PLAN_LIMITS } from "@/hooks/useSubscriptionManager";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(8);
  const [planDetails, setPlanDetails] = useState<string | null>(null);
  const [previousPlan, setPreviousPlan] = useState<UserType | null>(null);
  const [reportLimit, setReportLimit] = useState<number | null>(null);
  const sessionId = searchParams.get("session_id");
  const { handleSubscriptionUpgrade, refreshSubscriptionStatus } = useSubscriptionManager();

  useEffect(() => {
    // Verify the payment was successful
    const verifySession = async () => {
      try {
        console.log("Payment successful with session ID:", sessionId);

        if (!sessionId) {
          toast.warning("No payment session ID found");
          return;
        }

        // Get the current user
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          toast.error("User not found. Please sign in again.");
          navigate("/auth");
          return;
        }

        // Get the user's current profile to store previous plan type
        const { data: previousProfileData } = await supabase
          .from("profiles")
          .select("user_type")
          .eq("id", user.id)
          .single();

        if (previousProfileData) {
          setPreviousPlan(previousProfileData.user_type as UserType);
        }

        // Call the verify-payment edge function to confirm payment and update subscription
        const { data: verificationData, error: verificationError } =
          await supabase.functions.invoke("verify-payment", {
            body: {
              sessionId,
              userId: user.id,
            },
          });

        if (verificationError) {
          console.error("Error verifying payment:", verificationError);
          toast.error("Failed to verify payment");
          return;
        }

        if (!verificationData?.success) {
          console.error(
            "Payment verification failed:",
            verificationData?.message
          );
          toast.error(
            verificationData?.message || "Payment verification failed"
          );
          return;
        }

        // Payment verified successfully
        toast.success("Payment verified successfully!");

        // Get the new subscription details
        const newPlanType = verificationData.planType as UserType;

        // Handle subscription upgrade logic
        if (previousProfileData && newPlanType) {
          const previousPlanType = previousProfileData.user_type as UserType;

          // If this is an upgrade, handle it with our subscription manager
          if (previousPlanType !== newPlanType) {
            await handleSubscriptionUpgrade(
              user.id,
              newPlanType,
              previousPlanType
            );
          }
        }

        // Force refresh subscription data
        await refreshSubscriptionStatus(user.id);

        // Dispatch custom event to notify components about payment success
        window.dispatchEvent(new CustomEvent('payment-success'));

        // Get the updated user profile to display plan details
        const { data: profileData } = await supabase
          .from("profiles")
          .select("user_type")
          .eq("id", user.id)
          .single();

        if (profileData) {
          const planType = profileData.user_type as UserType;
          setReportLimit(
            PLAN_LIMITS[planType as keyof typeof PLAN_LIMITS] || 0
          );

          // Set a user-friendly plan name
          switch (planType) {
            case "starter":
              setPlanDetails("Starter Plan");
              break;
            case "pro":
              setPlanDetails("Pro Plan");
              break;
            case "premium":
              setPlanDetails("Premium Plan");
              break;
            case "free":
              setPlanDetails("Free Plan");
              break;
            default:
              setPlanDetails("Basic Access");
          }
        }

        // Show a prominent toast message about the new report allowance
        if (PLAN_LIMITS[newPlanType as keyof typeof PLAN_LIMITS]) {
          const limit = PLAN_LIMITS[newPlanType as keyof typeof PLAN_LIMITS];
          toast.success(`You can now generate ${limit} reports per month!`, {
            duration: 6000,
            position: "top-center",
          });
        }

        // Set a flag in session storage to show the welcome message in the chat UI
        sessionStorage.setItem("payment_success", "true");

        // Refresh the session to update auth context
        await supabase.auth.refreshSession();
      } catch (error) {
        console.error("Error verifying payment:", error);
        toast.error("Failed to verify payment");
      }
    };

    if (sessionId) {
      verifySession();
    } else {
      // If no session ID, show warning
      toast.warning("No payment session ID found");
    }

    // Start countdown for redirect
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Force a full page reload to ensure all state is refreshed
          window.location.href = "/chat";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionId, navigate, handleSubscriptionUpgrade, refreshSubscriptionStatus]);

  const handleDismiss = () => {
    // Force a full page reload to ensure all state is refreshed
    window.location.href = "/chat";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      <NavigationBar />

      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-gradient-to-b from-gray-800/70 to-gray-900/70 backdrop-blur-sm p-8 rounded-3xl border border-green-500/20 shadow-xl shadow-green-500/10">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-500">
            Payment Successful!
          </h1>

          <p className="text-lg text-gray-300 mb-4">
            Thank you for your purchase. Your transaction has been completed
            successfully.
          </p>

          {planDetails && (
            <div className="mb-6 p-6 bg-green-500/10 rounded-xl border border-green-500/20">
              <p className="text-green-400 font-medium text-xl mb-2">
                Your account has been upgraded to:{" "}
                <span className="font-bold">{planDetails}</span>
              </p>

              {reportLimit && (
                <div className="mt-3 p-4 bg-green-500/20 rounded-lg">
                  <p className="text-white text-lg font-bold">
                    {reportLimit === 1
                      ? "You can now generate 1 report!"
                      : `You can now generate ${reportLimit} reports per month!`}
                  </p>
                </div>
              )}

              {previousPlan === "starter" && planDetails === "Pro Plan" && (
                <p className="text-green-400 mt-4 text-sm">
                  You've been credited with your remaining reports from your
                  Starter plan!
                </p>
              )}
            </div>
          )}

          <div className="mb-8 p-4 bg-gray-800 rounded-xl">
            <p className="text-gray-400">
              You will be redirected to the chat in{" "}
              <span className="text-white font-bold">{countdown}</span> seconds.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleDismiss}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-xl"
            >
              Go to Chat Now
            </Button>

            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
